import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { readSkin, saveSkin, type Skin } from '../lib/skin.js';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push.js';
import { api, ApiError, type AiCheck, type Diagnostics, type Passkey } from '../lib/api.js';
import { PasskeyCancelled, passkeysSupported, registerPasskey } from '../lib/passkeys.js';
import { aiLevelAllows, type AiLevel, type PublicUser } from '@adhd/shared';
import { useSession } from '../lib/session.js';
import { useInstall } from '../lib/install.js';

/**
 * Account and sign-in settings.
 *
 * The passkey section says out loud that the password stays. Someone who reads
 * "passkey" as "replaces your password" and then loses the device would lose
 * access to their own health record, and the copy should not let that
 * misunderstanding form in the first place.
 */
/**
 * How warm the app sounds. One dimension, three positions.
 *
 * Register only. Never advising, never reassuring, and stopping everything for a
 * crisis are not points on this scale — "plain" is not colder about someone in
 * danger, and "warm" does not buy permission to say it will be fine.
 */
const AI_WARMTH_OPTIONS: { value: 'plain' | 'balanced' | 'warm'; label: string; description: string }[] = [
  {
    value: 'plain',
    label: 'Plain',
    description:
      'Says what happened and stops. Short, factual, nothing added on the end. Not cold — just not an arm round the shoulder.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'The default. Straightforward, with room to be kind where it is called for.',
  },
  {
    value: 'warm',
    label: 'Warm',
    description:
      'Writes like someone who is fond of you and has been paying attention — quotes the specific thing you said back, and reacts to it. Still never advises, reassures, or looks for the silver lining.',
  },
];

/**
 * What this server is running, and whether the model behind it answers.
 *
 * The rows are the ones that were always here. What was missing was any way to
 * tell whether "AI features: anthropic" meant anything: `/capabilities`
 * resolves that to a provider name whenever a key STRING is set, and says the
 * same thing once the key is revoked. Measured on a study server given a
 * deliberately invalid key — it reported itself healthy while every call behind
 * it 401'd.
 *
 * So: one button that makes a real call, and the answer underneath it. Nothing
 * runs on load — a check on every visit would bill him for a model call to
 * answer a question he had not asked — and the counts live behind a fold,
 * because none of them help with "is it working" and a settings screen that
 * opens onto a wall of numbers is one nobody reads.
 */
/**
 * Adding it to the home screen, from a button rather than a menu.
 *
 * Chrome will hand a page its install dialog; Safari will not, and Apple offers
 * no way to ask. So this is two different sections wearing one heading: a real
 * button where a real button is possible, and otherwise the one sentence that
 * describes the manual route — which is genuinely all iOS allows, and saying so
 * plainly beats a button that does nothing.
 *
 * Absent entirely once installed. A permanent "install me" on a screen you
 * reached FROM the installed app is the kind of thing that makes software feel
 * like it is not paying attention.
 */
function InstallSection(): JSX.Element | null {
  const state = useInstall();
  if (state.kind === 'installed' || state.kind === 'none') return null;

  return (
    <section className="stack">
      <h2>Put it on your home screen</h2>
      <p className="muted">
        It opens like an app — its own icon, no browser around it, and it still works with no
        connection.
      </p>
      {state.kind === 'prompt' ? (
        <button className="btn btn-secondary btn-block" onClick={() => void state.install()}>
          Add to home screen
        </button>
      ) : (
        <div className="card stack-sm stack">
          <p className="muted" style={{ margin: 0 }}>
            {/* Not a button, because iOS has no way to offer one. Naming the
                exact menu item is the whole value here — the Share icon is the
                square with the arrow, and people look for a plus. */}
            In Safari: tap <strong>Share</strong> — the square with an arrow out of it — then{' '}
            <strong>Add to Home Screen</strong>.
          </p>
          <span className="faint">
            It has to be Safari. Chrome and Firefox on iPhone cannot add it.
          </span>
        </div>
      )}
    </section>
  );
}

function AppDiagnostics({ onGoToAi }: { onGoToAi: () => void }): JSX.Element {
  const [d, setD] = useState<Diagnostics | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [check, setCheck] = useState<AiCheck | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .diagnostics()
      .then((r) => {
        if (live) setD(r);
      })
      .catch((e) => {
        if (live) setFailed(e instanceof ApiError ? e.message : 'Could not read this.');
      });
    return () => {
      live = false;
    };
  }, []);

  const runCheck = useCallback(async () => {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await api.aiCheck());
    } catch (e) {
      setCheck({
        ok: false,
        configured: true,
        error: e instanceof ApiError ? e.message : 'The check itself failed.',
      });
    } finally {
      setChecking(false);
    }
  }, []);

  if (failed) return <p className="muted">{failed}</p>;
  if (!d) return <p className="muted">Reading…</p>;

  const { configured: c, inForce, counts, storage } = d;
  // His choice, which is a different thing from the server having no key.
  const aiOff = inForce.aiLevel === 'off';

  return (
    <div className="stack-sm stack">
      <div className="card stack-sm stack">
        {/* AI first, because transcription is AI too and was reading as the
            senior of the two by sitting above it. */}
        <div className="row-between">
          <span className="muted">AI features</span>
          <span className="server-value">
            {/*
              Whose "off" it is, and the provider only if it is not off at all.
              This said "anthropic" with AI switched off — naming the provider
              of calls that were never going to be made, which is the same
              failure as reporting `reasoning: true` on a dead key: a fact about
              configuration standing in for a fact about what happens.

              The model name is not here. In this row it pushed the label onto
              two lines and the value onto two more, on a phone; it is in
              Advanced info with the other detail.
            */}
            <strong>{aiOff ? 'off' : !c.reasoning ? 'not set up' : `on · ${c.reasoningProvider}`}</strong>
            {/* In the row it describes, rather than a block button underneath.
                It answers a question about this one line, and a full-width
                control made it look like the main thing on the screen. */}
            {!aiOff && c.reasoning && (
              <button className="btn-inline" onClick={() => void runCheck()} disabled={checking}>
                {checking ? 'testing…' : 'test'}
              </button>
            )}
          </span>
        </div>
        {check && (
          <p className={check.ok ? 'test-ok' : 'test-bad'}>
            {check.ok ? `✓ Answered in ${check.ms} ms` : `✕ ${check.error ?? 'It did not answer.'}`}
          </p>
        )}
        {/* Both read from the running process rather than the defaults in the
            source: the voice in voice.ts and the value fly.toml was pinning
            disagreed for a fortnight, and no screen could have shown it. */}
        <Row
          label="Voice transcription"
          value={aiOff ? 'off' : !c.transcription ? 'not set up' : `on · ${c.transcriptionProvider}`}
        />
        {/* Both describe how a model would sound. With AI off nothing is ever
            going to say anything, and a row naming the voice that will not
            speak is the same untruth as the one above it. */}
        {!aiOff && <Row label="Voice" value={inForce.voice} />}
        {!aiOff && (
          <Row label="Tone" value={AI_WARMTH_OPTIONS.find((o) => o.value === inForce.warmth)?.label ?? inForce.warmth} />
        )}
        <Row label="Encrypted at rest" value={c.encryptionAtRest ? 'on' : 'off'} />
        <Row label="New sign-ups" value={c.signupAllowed ? 'open' : 'closed'} />
        {/*
          TWO BUILDS, AND WHETHER THEY MATCH — which is the only question these
          rows were ever for.

          "Version" used to be the string '0.1.0', hard-coded since the first
          commit, so it sat next to a live browser timestamp looking comparable
          and could never differ from itself. The row that existed to answer
          "am I looking at what is deployed?" was the one row guaranteed not to.

          Now the server reports the build it is SERVING and the page reports
          the build it is RUNNING, and the app says outright when they differ
          instead of leaving two numbers to be eyeballed. An installed service
          worker can serve a page from weeks ago and nothing else on screen
          says so.
        */}
        <Row label="Server is serving" value={c.version} />
        <Row label="This browser is running" value={BUILD_ID} />
        {BUILD_ID !== 'dev' && c.version !== 'dev' && (
          <Row
            label="Up to date"
            value={BUILD_ID === c.version ? 'yes' : 'no — this page is an old copy'}
          />
        )}
      </div>

      <details className="diag-more">
        <summary>Advanced info</summary>
        <div className="card stack-sm stack">
          {/* A link, because this is the one row here that is a setting rather
              than a fact — reading it is usually the moment you want to change
              it, and the tabs are state rather than routes so it cannot be an
              <a>. */}
          <div className="row-between">
            <span className="muted">How much AI</span>
            <button className="btn-inline" onClick={onGoToAi}>
              {AI_LEVEL_OPTIONS.find((o) => o.value === inForce.aiLevel)?.label ?? inForce.aiLevel}
            </button>
          </div>
          <Row label="Model" value={c.reasoning ? c.reasoningModel : '—'} />
          <Row label="Transcription model" value={c.transcription ? c.transcriptionModel : '—'} />
          <Row label="Time zone" value={inForce.timezone} />
          <Row label="Crisis numbers" value={c.crisisRegion} />
          <Row label="Check-ins" value={counts.checkins.toLocaleString()} />
          <Row label="Journal entries" value={counts.journalEntries.toLocaleString()} />
          <Row label="Thought records" value={`${counts.thoughtRecords} · ${counts.thoughtRecordsCompleted} worked through`} />
          {/* Marked, not "recorded": this counts the doses he tapped taken,
              missed or skipped. The adherence figures elsewhere also count doses
              the app assumed, so "46 recorded" beside "47 of 54 taken" looked
              like a contradiction when it was two different counts. */}
          <Row label="Doses you marked" value={`${counts.doses.toLocaleString()} across ${counts.medications}`} />
          <Row label="Help now moments" value={counts.sosEvents.toLocaleString()} />
          <Row label="Threads marked" value={counts.lifeEvents.toLocaleString()} />
          <Row label="Weekly write-ups" value={counts.weeklyReports.toLocaleString()} />
          <Row label="Days logged" value={counts.firstDay ? `${counts.daysLogged}, since ${counts.firstDay}` : String(counts.daysLogged)} />
          {/* Openable right now — not revoked, not expired. How many links were
              ever made is not the number worth knowing. */}
          <Row label="Share links working now" value={counts.activeShares.toLocaleString()} />
          <Row label="Database" value={mb(storage.databaseBytes)} />
          <Row label="Recordings" value={mb(storage.audioBytes)} />
        </div>
      </details>
    </div>
  );
}

const mb = (bytes: number): string =>
  bytes < 1024
    ? `${bytes} bytes`
    : bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;


const AI_LEVEL_OPTIONS: { value: AiLevel; label: string; description: string }[] = [
  {
    value: 'converse',
    label: 'It can talk with me',
    description:
      'Everything below, and it will talk it through with you — out loud or typed — knowing what you have already told the app, so it does not ask what it could have read.',
  },
  {
    value: 'reflect',
    label: 'It can read how I am doing',
    description:
      'Everything below, and it forms a view about you: how heavy an entry was, how the day went, whether something bigger is going on. A share link can carry what it writes: the daily lines, today\u2019s and each of the six days behind it, the topic words on each day, and the weekly readings made from them, how the week seemed and what the week held.',
  },
  {
    value: 'summarise',
    label: 'It can write my entries up',
    description:
      'Everything below, and each entry gets a title, a short recap and its subjects, so a month of them is skimmable; an Everything share link carries the titles and recaps. The subjects are yours to change on the entry — the app\u2019s picks show dashed until you take them off. A recap says what you said — never a view about you. From an entry it can also draft a worksheet for you to check and change, and any thinking traps it suggests there are marked as its picks. Weekly round-ups are written here too; a share link shows yours only at the Everything level.',
  },
  {
    value: 'transcribe',
    label: 'It can write down what I say',
    description:
      'Spoken entries are turned into text, word for word, so they can be read and searched. Nothing is summarised and nothing forms a view. The first setting where anything leaves this server: to help it spell, each recording goes with the names of your medications and your recent check-in chips.',
  },
  {
    value: 'off',
    label: 'Off — nothing leaves this server',
    description:
      // Says what still works, because the reasonable fear at this setting is
      // that "off" means a crippled app — and it does not. This is the state
      // the app runs in with no API keys at all, which the test suite exercises
      // on every run.
      'No model is called for anything from now on. Anything already written — entry titles, recaps, weekly round-ups — stays exactly where it is; this stops new ones, it does not erase old ones. Recordings stay as audio you can play back, and typed entries stay exactly as you wrote them. Your check-ins, medications, thought records, trends, sharing and printing all work exactly as they do now. Marking something big still works too — it is at the foot of “How today’s going”.',
  },
];
/**
 * Bringing your own AI account.
 *
 * A key typed here beats the environment variable, so using your own account
 * stops being a redeploy and becomes a setting.
 *
 * WHAT THIS IS NOT, said on screen rather than left to be discovered: there is
 * no way to run this app on a ChatGPT subscription. "Sign in with ChatGPT" went
 * to beta in August 2026 and hands a third-party app a name, an email and a
 * picture — identity, not model access. Billing model calls to somebody's plan
 * is for OpenAI's own tools, and a Plus subscription grants no API access at
 * all. Somebody who does not know that will paste their ChatGPT password in and
 * wonder why nothing works.
 *
 * The key is write-only. It is never fetched back, not even masked: "we only
 * show the last four" is how a live credential ends up in a screenshot.
 */
function AiAccount(): JSX.Element {
  const [state, setState] = useState<Awaited<ReturnType<typeof api.aiKeys>> | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api.aiKeys().then(setState).catch(() => setState(null));
  }, []);
  useEffect(load, [load]);

  if (!state) return <></>;

  const save = (): void => {
    setBusy(true);
    setErr(null);
    void api
      .setAiKey('openai', draft.trim())
      .then(() => {
        setDraft('');
        load();
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not save that.'))
      .finally(() => setBusy(false));
  };

  return (
    <section className="stack">
      <h2>AI account</h2>
      <p className="muted">
        By default this uses whatever key the server was started with. Paste your own to use your
        own account and your own bill instead.
      </p>

      <div className="card stack-sm stack">
        <strong>
          {state.reasoning === 'none'
            ? 'No AI configured'
            : `Using ${state.reasoning === 'openai' ? 'OpenAI' : 'Anthropic'}${
                state.openai.own || state.anthropic.own ? ' — your key' : " — this server's key"
              }`}
        </strong>
        <span className="faint">
          {state.transcription
            ? 'Recordings can be written up.'
            : 'Recordings will be saved as audio only.'}
        </span>
      </div>

      {!state.canStore && (
        <div className="banner banner-caution">
          This server has no <code>ENCRYPTION_KEY</code> set, so a key typed here would be stored in
          plain text — and end up in every backup. Set one and restart, or keep using the
          environment variable.
        </div>
      )}

      {state.openai.own ? (
        <button
          className="btn btn-ghost btn-block"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void api.clearAiKey('openai').then(load).finally(() => setBusy(false));
          }}
        >
          Remove my OpenAI key
        </button>
      ) : (
        <div className="field">
          <label htmlFor="openai-key">Your OpenAI API key</label>
          <input
            id="openai-key"
            className="input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={draft}
            disabled={!state.canStore || busy}
            onChange={(e) => setDraft(e.target.value)}
          />
          <p className="faint" style={{ marginTop: 'var(--space-2)' }}>
            A key from platform.openai.com, starting <code>sk-</code>. This is not your ChatGPT
            login, and a ChatGPT Plus subscription does not include API access — it is billed
            separately. Stored encrypted here and never shown again.
          </p>
          {err && <div className="banner banner-error">{err}</div>}
          <button
            className="btn btn-secondary btn-block"
            disabled={!state.canStore || busy || draft.trim().length < 20}
            onClick={save}
          >
            {busy ? <span className="spinner" /> : 'Use this key'}
          </button>
        </div>
      )}
    </section>
  );
}

export function Settings(): JSX.Element {
  const navigate = useNavigate();
  const { user, signOut, refresh } = useSession();
  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const level = user?.aiLevel ?? 'converse';
  const aiOff = level === 'off';

  async function setReminder(on: boolean): Promise<void> {
    setSavingReview(true);
    setReviewError(null);
    try {
      await api.updateProfile({ lifeEventReminder: on });
      await refresh();
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setSavingReview(false);
    }
  }

  async function setLevel(level: AiLevel): Promise<void> {
    setSavingReview(true);
    setReviewError(null);
    try {
      await api.updateProfile({ aiLevel: level });
      // The session holds the user object every screen reads, so it has to be
      // told — otherwise this setting appears to revert on the next render.
      await refresh();
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setSavingReview(false);
    }
  }

  /*
   * Tabbed rather than one long column.
   *
   * Six sections had accumulated here and the screen had become a wall you
   * scroll past rather than read — which matters most for the two settings that
   * change what the app discloses about you, because those are the ones that
   * should be easy to find and easy to re-check.
   *
   * Not a router: a settings tab is not a place you link someone to, and putting
   * it in the URL would add a back-button trap for no benefit.
   *
   * It does take an opening tab from ROUTER STATE, which is a different thing.
   * "No written read at this AI setting — turn it on" on the day summary has to
   * land on the rung list or it is just a signpost to a screen with six tabs.
   * Router state is not in the URL, so the objection above still holds: nothing
   * to share, nothing to come back to, and Settings reached any other way still
   * opens where it always did.
   */
  const [skin, setSkin] = useState<Skin>(() => readSkin());

  const opening = (useLocation().state as { tab?: string } | null)?.tab;
  const [tab, setTab] = useState<'account' | 'ai' | 'look' | 'sharing' | 'app'>(
    opening === 'ai' || opening === 'look' || opening === 'sharing' || opening === 'app'
      ? opening
      : 'account',
  );

  async function setWarmth(warmth: 'plain' | 'balanced' | 'warm'): Promise<void> {
    setSavingReview(true);
    setReviewError(null);
    try {
      await api.updateProfile({ aiWarmth: warmth });
      // The session holds the user object every screen reads, so it has to be
      // told — otherwise this setting appears to revert on the next render.
      await refresh();
    } catch (e) {
      setReviewError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setSavingReview(false);
    }
  }

  const supported = passkeysSupported();

  useEffect(() => {
    api.listPasskeys().then(setPasskeys).catch(() => setPasskeys([]));
  }, []);

  async function add(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await registerPasskey(defaultLabel());
      setPasskeys(await api.listPasskeys());
      setNotice('Passkey added. You can use it to sign in from now on.');
    } catch (err) {
      if (!(err instanceof PasskeyCancelled)) {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    if (!confirm('Remove this passkey? You can always add it again.')) return;
    await api.deletePasskey(id).catch(() => {});
    setPasskeys(await api.listPasskeys().catch(() => []));
  }

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/')}
        >
          <ArrowLeftIcon /> Back to today
        </button>
        <h1>Settings</h1>
        <p>{user?.email}</p>
      </header>

      {/* Chips rather than a segmented control: there are five of these and a
          phone is narrow, so the row has to be allowed to scroll rather than
          crush five labels into unreadable stubs. */}
      <div className="chips settings-tabs" role="tablist">
        {(
          [
            ['account', 'Account', null],
            ['ai', 'AI', null],
            ['look', 'Theme', null],
            ['sharing', 'Sharing', null],
            // Not a tab — a destination. Medication was once a tab whose entire
            // content was a single link card to /medications, so reaching your
            // own list took two taps: one to open a page, one to leave it. It
            // belongs in this row because that is where you look for it, in its
            // place in the order rather than tacked on the end, and styled
            // exactly like its neighbours — a control that looks different for a
            // reason nobody can see just looks broken.
            ['medications', 'Meds', '/medications'],
            ['app', 'App', null],
          ] as const
        ).map(([key, label, to]) =>
          to ? (
            <Link key={key} className="chip" to={to}>
              {label}
            </Link>
          ) : (
            <button
              key={key}
              role="tab"
              className="chip"
              aria-selected={tab === key}
              aria-pressed={tab === key}
              onClick={() => setTab(key as typeof tab)}
            >
              {label}
            </button>
          ),
        )}
      </div>

      {tab === 'account' && (
      <section className="stack">
        <h2>Signing in</h2>

        {notice && <div className="banner banner-positive">{notice}</div>}
        {error && <div className="banner banner-error">{error}</div>}

        {!supported ? (
          <div className="banner">
            This browser doesn't support passkeys. Your password works as normal.
          </div>
        ) : (
          <>
            <p className="muted">
              A passkey lets you sign in with Face ID, Touch ID, or your device PIN instead of
              typing a password. It stays on your devices — nothing is sent to Google or Apple, and
              there's nothing to expire.
            </p>

            {passkeys && passkeys.length > 0 && (
              <div className="stack-sm stack">
                {passkeys.map((p) => (
                  <div className="card" key={p.id}>
                    <div className="row-between">
                      <div>
                        <strong>{p.label ?? 'Passkey'}</strong>
                        <p className="faint" style={{ marginTop: 2 }}>
                          Added {formatDate(p.createdAt)}
                          {p.lastUsedAt ? ` · last used ${formatDate(p.lastUsedAt)}` : ' · not used yet'}
                        </p>
                        {/* A device-bound passkey disappears with the device. Worth
                            knowing before you rely on it as your only one. */}
                        {!p.backedUp && (
                          <p className="faint">Only on this device — it won't sync elsewhere.</p>
                        )}
                      </div>
                      <button className="btn btn-ghost btn-ghost-quiet btn-compact" onClick={() => void remove(p.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-secondary btn-block" onClick={() => void add()} disabled={busy}>
              {busy ? <span className="spinner" /> : passkeys && passkeys.length > 0 ? 'Add another passkey' : 'Set up a passkey'}
            </button>

            <div className="banner">
              <strong>Your password still works.</strong> Passkeys sit alongside it rather than
              replacing it — if you lost the device holding your only passkey, you'd still need a
              way in.
            </div>
          </>
        )}
      </section>
    )}

      {/*
        * How much of what you write gets read.
        *
        * A setting rather than a decision made on someone's behalf. The
        * difference between an app that notices and an app that watches is
        * entirely whether the person asked for it, and that is not a judgement
        * to make for them about their own journal.
        */}
      {tab === 'ai' && (
      <section className="stack">
        <h2>How much AI</h2>
        <p className="muted">
          One setting, not a scatter of switches. Each step does everything the step below it does,
          and a bit more. Nothing here turns the app off — your check-ins, medications, thought
          records, trends, sharing and printing work the same at every setting, including off.
          {' '}
          {/* Said once, here, rather than in five descriptions. Both directions
              matter and only one of them is obvious: people expect switching
              off to stop things, and are surprised either way about what
              happens to what already exists. */}
          <strong>
            This only changes what happens next. Turning it down leaves everything already written
            where it is, and turning it up does not go back and write about days that are over.
          </strong>
        </p>
        <div className="stack-sm stack">
          {AI_LEVEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className="option"
              aria-pressed={(user?.aiLevel ?? 'converse') === opt.value}
              disabled={savingReview}
              onClick={() => void setLevel(opt.value)}
            >
              <strong>{opt.label}</strong>
              <span>{opt.description}</span>
            </button>
          ))}
        </div>
        {reviewError && <div className="banner banner-error">{reviewError}</div>}
      </section>
    )}

      {/* Medications used to live here in full, which was the wrong place for
          the one feature in the app with a time of day attached to it: the
          failure mode is forgetting, and a screen you have to remember to open
          does not help you remember. Today's doses are on Today now, and this
          is only the way back to the list itself — kept because someone
          reviewing what they are on is doing exactly what people come to
          Settings to do, and because with no medications added yet there is
          nothing on Today to tap through. */}
      {/* Every one of these existed only as a URL you had to already know, which
          is the same as not existing. This is where someone goes looking when
          the question is "how do I get this to my therapist", so it is where the
          ways out of the app live. */}
      {/* Not at Off. A field for configuring an account that will never be
          called is precisely the clutter the ladder exists to remove.

          Hidden rather than disabled, and above Off rather than at it, because
          that is also the right order to meet it in: choose what you want the
          app to do, and only then be told a key is needed for it. Offering the
          key first asks somebody to set up a prerequisite for something they
          have not yet asked for. */}
      {/* Not on the AI tab: nothing about this needs a model, and it works
          identically at every rung including off. It belongs with the app's own
          behaviour. */}
      {tab === 'app' && <InstallSection />}

      {/*
        THE MANUAL, at the top of the App tab rather than buried at the bottom:
        this is the tab somebody opens when they do not understand something,
        and the answer should not be below the button that clears their cache.

        A PLAIN <a>, NOT <Link>. /help is a static page the server hands over
        directly (packages/server/src/app.ts) — it is not a route in this
        router, so a <Link> would be handled client-side, match nothing, and
        bounce to "/". target="_blank" so reading it does not throw away
        whatever they were part-way through here.
      */}
      {tab === 'ai' && user && (
        <section className="stack-sm stack">
          <h2>The door</h2>
          <p className="muted">
            What you say on Today is saved as an entry first, always. With this on, and the AI setting at
            “write my entries up” or above, the app then proposes what to file from it — a mood word, a
            dose, a mark, who you were with — each quoting your words. Nothing is written until you tap
            File it, unless you turn “Ask before filing” off. “Just save it” and the slider never involve it.
          </p>
          <label className="option option-toggle">
            <input type="checkbox" checked={user.doorFiling} onChange={(e) => void api.updateProfile({ doorFiling: e.target.checked }).then(() => refresh())} />
            <span><strong>File what I say</strong><span className="muted"> — on by default</span></span>
          </label>
          <label className="option option-toggle">
            <input type="checkbox" checked={user.doorAsks} disabled={!user.doorFiling} onChange={(e) => void api.updateProfile({ doorAsks: e.target.checked }).then(() => refresh())} />
            <span><strong>Ask before filing</strong><span className="muted"> — on by default: the receipts wait for File it. Off, the app files its picks as soon as it has read the entry, and × on a receipt takes that one back out.</span></span>
          </label>
          <label className="option option-toggle">
            <input type="checkbox" checked={user.hideMarksAtRest} onChange={(e) => void api.updateProfile({ hideMarksAtRest: e.target.checked }).then(() => refresh())} />
            <span><strong>Hide marks at rest</strong><span className="muted"> — Today shows “Marks · 2” until you tap it, so a phone left face-up says nothing. Off by default.</span></span>
          </label>
          <label className="option option-toggle">
            <input type="checkbox" checked={user.ownWordsBack} onChange={(e) => void api.updateProfile({ ownWordsBack: e.target.checked }).then(() => refresh())} />
            <span><strong>Hand me my own earlier words</strong><span className="muted"> — when what you say sounds like the shape of an earlier worksheet about the same person, show the fairer read you wrote on it, with the thought it answered. One, yours, word for word; the app writes nothing here.</span></span>
          </label>
          <button type="button" className="linkish linkish-quiet" onClick={() => { if (window.confirm('Forget every pick you have taken off? The app will offer them again.')) void api.clearDoorCorrections(); }}>
            Forget what the door has learned from my corrections
          </button>
        </section>
      )}

      {tab === 'app' && user && (
        <section className="stack-sm stack">
          <h2>The day's faces</h2>
          <p className="muted">
            Today shows a morning face until the morning ends, the night face from the night starts, and
            Now in between. The clock picks; the link at the foot of each face moves between them. Set the
            two times for your own day.
          </p>
          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <div className="field grow">
              <label htmlFor="morning-ends">Morning ends</label>
              <input
                id="morning-ends"
                className="input"
                type="time"
                value={user.morningEndsAt}
                onChange={(e) => {
                  // On change, not blur: an iPhone's wheel never blurs until
                  // you tap elsewhere, so a chosen time was not being saved.
                  if (/^\d\d:\d\d$/.test(e.target.value)) void api.updateProfile({ morningEndsAt: e.target.value }).then(() => refresh());
                }}
              />
            </div>
            <div className="field grow">
              <label htmlFor="night-starts">Night starts</label>
              <input
                id="night-starts"
                className="input"
                type="time"
                value={user.nightStartsAt}
                onChange={(e) => {
                  if (/^\d\d:\d\d$/.test(e.target.value)) void api.updateProfile({ nightStartsAt: e.target.value }).then(() => refresh());
                }}
              />
            </div>
          </div>
        </section>
      )}

      {tab === 'app' && user && <SleepFromHealth user={user} refresh={refresh} />}

      {tab === 'app' && user && <TwoADay user={user} refresh={refresh} />}

      {tab === 'app' && (
        <section className="stack-sm stack">
          <h2>How this works</h2>
          <p className="muted">
            A plain-English manual: what each screen is for, what the AI settings change, and
            exactly what a share link does and does not pass on. It has its own section for
            whoever you share with.
          </p>
          <a
            className="btn btn-secondary btn-block"
            href="/help"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the manual
          </a>
        </section>
      )}

      {tab === 'app' && (
        <section className="stack">
          <h2>Threads</h2>
          <p className="muted">
            A thread is a life event, big or small — something shaping your days. The ones you mark change
            how your days are read, and keep doing it until you close them off on their own page. Nothing
            asks about them unless you want it to.
          </p>
          <button
            className="option option-toggle"
            aria-pressed={user?.lifeEventReminder ?? false}
            disabled={savingReview}
            onClick={() => void setReminder(!(user?.lifeEventReminder ?? false))}
          >
            <strong>
              Remind me to check in on these
              {/* The state as a word, because this is a switch and not one of a
                  list. "Off by default" in the description below is about the
                  default, not about where this is set now — it reads the same
                  either way, which is why the card could not be told apart from
                  itself. */}
              <span className="option-state">
                {(user?.lifeEventReminder ?? false) ? 'On' : 'Off'}
              </span>
            </strong>
            <span>
              Once every eight weeks, at the foot of “How today’s going”, for anything still
              running. Either answer buys another eight weeks. Off by default — an app that raises
              the hardest thing in your year on a timer is a worse companion than one that waits to
              be asked.
            </span>
          </button>
        </section>
      )}

      {tab === 'ai' && !aiOff && <AiAccount />}

      {/*
        Hidden below `reflect`, which is the first rung where anything reads it.
        This was `summarise`, one rung too early: the entry summariser takes no
        warmth at all — ai/index.ts says so and drops the parameter rather than
        defaulting it — so at "It can write my entries up" the control appeared,
        did nothing, and described two things that rung cannot do. A setting
        that is visible and inert is worse than one that is hidden, because it
        invites the belief that it was tried and made no difference.

        The three readers of warmth are the daily read (`reflect`) and the two
        halves of the companion (`converse`).
      */}
      {tab === 'ai' && aiLevelAllows(level, 'reflect') && (
      <section className="stack">
        <h2>Tone</h2>
        <p className="muted">
          How it sounds when it writes about your day or talks with you. This changes the wording
          only — it never changes what the app will and won't say. No advice, no reassurance, and
          stopping for a crisis are fixed at every setting.
        </p>
        <div className="stack-sm stack">
          {AI_WARMTH_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className="option"
              aria-pressed={(user?.aiWarmth ?? 'balanced') === opt.value}
              disabled={savingReview}
              onClick={() => void setWarmth(opt.value)}
            >
              <strong>{opt.label}</strong>
              <span>{opt.description}</span>
            </button>
          ))}
        </div>
      </section>
      )}

      {tab === 'look' && (
      <section className="stack">
        <h2>Appearance</h2>
        <p className="muted">
          Only how it looks. Nothing moves, nothing is hidden, and every screen works exactly the
          same either way.
        </p>
        {/* Per device on purpose — see lib/skin.ts. The laptop in a bright room
            and the phone at 1am are not asking for the same palette. */}
        <div className="stack-sm stack">
          {(
            [
              [
                'sky',
                'Sky',
                'Dark in every light, and the top of Today takes the colour of your latest check-in. The 2.0 look, and the default since 13 October. Nothing is a score.',
              ],
              ['default', 'Standard', 'Cool ink on cream, sage green. Calm and a little clinical.'],
              [
                'soft',
                'Soft paper',
                'Warm paper, warm ink, rounder corners and no pure white anywhere. Easier on tired eyes, and a bit friendlier to open when you feel bad.',
              ],
              [
                'sketch',
                'Sketchbook',
                'Notebook paper with faint ruled lines, a red margin and a few doodles in the background. Ink blue and coral, and no two cards quite the same shape. The drawings stay behind the page — never under anything you have to read.',
              ],
              [
                'evening',
                'Evening',
                'Deep blue-green, low light, no white anywhere. Built for the hour this actually gets opened — a different room rather than the same room with the lights off. Works in daylight too.',
              ],
              [
                'crayon',
                'Crayon',
                'Sugar paper and wax crayon. Peach page, grape ink, corners rounder than anything else here, and a sun and a rainbow drawn faintly behind it all. The silly one — friendly rather than loud, because a theme that shouts is one you stop picking by Wednesday.',
              ],
            ] as const
          ).map(([value, label, blurb]) => (
            <button
              key={value}
              className="option"
              aria-pressed={skin === value}
              onClick={() => {
                setSkin(value);
                saveSkin(value);
              }}
            >
              <strong>
                {label}
                
              </strong>
              <span>{blurb}</span>
            </button>
          ))}
        </div>
        <p className="faint">
          This is remembered on this device only, so your phone and your laptop can differ. Light
          and dark still follow whatever your system is set to.
        </p>
      </section>
      )}

      {tab === 'sharing' && (
      <section className="stack">
        <h2>Sharing and export</h2>

        <Link className="option" to="/circle">
          <strong>Circle</strong>
          <span>
            Make a link that shows one level of your record, stops working on a date you choose,
            and can be switched off at any time.
          </span>
        </Link>

        <Link className="option" to="/export">
          <strong>Print or save your records</strong>
          <span>
            Lay your thought records out as a document to print or keep as a PDF — a copy that then
            lives outside the app, wherever you leave it.
          </span>
        </Link>

        {/* Under a heading that does not quite describe it, and deliberately so.
            Nothing of yours leaves the app on a blank sheet, but this is the
            section people scan when the question is about printing, and the
            entry it replaces was worse: blanks were a checkbox part-way down the
            export screen, so the only route to empty paper ran through a
            document of your own records. */}
        <Link className="option" to="/worksheets">
          <strong>Blank worksheets</strong>
          <span>
            Printable empty worksheets to fill in by hand, three situations to a page.
          </span>
        </Link>
      </section>
    )}

      {tab === 'app' && (
        <section className="stack">
          <h2>This server</h2>
          <AppDiagnostics
            onGoToAi={() => {
              setTab('ai');
              window.scrollTo({ top: 0 });
            }}
          />
        </section>
      )}

      {tab === 'app' && (
      <section className="stack-sm stack">
        <h2>If something looks out of date</h2>
        <p className="muted">
          The app keeps a copy of itself on your device so it opens without a connection. Very
          occasionally that copy gets stuck and you carry on seeing an old version — a missing
          button is the usual sign. This throws the copy away and reloads.
        </p>
        <button className="btn btn-secondary btn-block" onClick={() => void hardRefresh()}>
          Clear the local copy and reload
        </button>
      </section>
    )}

      <button className="btn btn-secondary btn-block" onClick={() => void signOut()}>
        Sign out
      </button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="row-between">
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** Stamped in at build time by vite.config.ts. */
declare const __BUILD_ID__: string;
const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

/**
 * Throw away the cached copy of the app and come back on the network.
 *
 * Unregistering the worker is the part that matters — the caches go with it on
 * the next load, but deleting them here too means the reload cannot be answered
 * out of a cache the dead worker left behind. `reload()` is not enough on its
 * own: a controlling worker answers the reload as happily as it answered the
 * first load, which is why "just refresh" kept not working.
 */
async function hardRefresh(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // Whatever failed, the reload below is still worth doing.
  }
  // Cache-busted so the navigation cannot be served from the HTTP cache either.
  window.location.replace(`/?fresh=${Date.now()}`);
}


/** A guess at which device this is, so a list of passkeys is tellable apart. */
function defaultLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  return 'This device';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}


/**
 * Two a day (docs/PRD-2.0.md §2): one switch, off by default. Enabling asks
 * the phone once and registers this device; the server sends a fixed line at
 * the morning time and when the night face starts — never a count, a streak,
 * a "you haven't", or a question. On an iPhone it works only for the app on
 * the home screen, and the row says so instead of failing quietly.
 */
function TwoADay({ user, refresh }: { user: PublicUser; refresh: () => void }): JSX.Element {
  const [state, setState] = useState<PushState | 'checking'>('checking');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    void pushState().then(setState).catch(() => setState('unsupported'));
  }, []);
  const on = user.notifications && state === 'on';
  async function flip(next: boolean): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      if (next) {
        const s = await enablePush();
        setState(s);
        if (s === 'on') await api.updateProfile({ notifications: true });
        else if (s === 'denied') setNote('The phone said no. Allow notifications for this app in its settings, then try again.');
      } else {
        await api.updateProfile({ notifications: false });
        setState(await disablePush());
      }
      refresh();
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }
  async function test(): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      const r = await api.pushTest();
      setNote(r.sent > 0 ? 'Sent. It should arrive in a moment.' : 'Nothing to send to: this device is not registered.');
    } catch (e) {
      setNote(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="stack-sm stack">
      <h2>Notifications</h2>
      <p className="muted">
        Two a day, to this phone: one in the morning and one when the night face starts. The same few words every time — never a count, a streak, or a “you haven't”. Off unless you turn it on.
      </p>
      {state === 'checking' ? (
        <span className="spinner" />
      ) : state === 'unsupported' ? (
        <p className="faint">This browser cannot receive notifications.</p>
      ) : state === 'not-installed' ? (
        <p className="faint">On an iPhone this works only once the app is on your home screen: Share → Add to Home Screen, then open it from there.</p>
      ) : state === 'not-configured' ? (
        <p className="faint">Not set up on this server: it needs its push keys. Nothing can be sent until it has them.</p>
      ) : (
        <>
          <label className="option option-toggle">
            <input type="checkbox" checked={on} disabled={busy || state === 'denied'} onChange={(e) => void flip(e.target.checked)} />
            <span><strong>Send me notifications</strong><span className="muted">{state === 'denied' ? ' — this phone has refused them for this app' : on ? ' — on, for this phone' : ' — off'}</span></span>
          </label>
          {on && (
            <div className="row" style={{ gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field">
                <label htmlFor="morning-at">The morning one, at</label>
                <input id="morning-at" className="input" type="time" value={user.morningAt} onChange={(e) => { if (/^\d\d:\d\d$/.test(e.target.value)) void api.updateProfile({ morningAt: e.target.value }).then(() => refresh()); }} />
              </div>
              <span className="faint">The night one comes when the night face starts ({user.nightStartsAt}, set above), and not at all once the day is closed.</span>
              <button type="button" className="btn btn-secondary btn-compact" disabled={busy} onClick={() => void test()}>Send a test</button>
            </div>
          )}
        </>
      )}
      {note && <p className="faint" role="status">{note}</p>}
    </section>
  );
}

/**
 * Sleep from Health (docs/pitch-2026-10-12/fixes/mocks/sleep.html; 14 Oct 2026).
 * One switch, the two boundaries, a key shown once, and the recipe for the
 * Shortcut. The key can write one word for one day and nothing else.
 */
function SleepFromHealth({ user, refresh }: { user: PublicUser; refresh: () => Promise<void> | void }): JSX.Element {
  const [shown, setShown] = useState<string | null>(null);
  const [state, setState] = useState<{ hasKey: boolean; createdAt: string | null; lastUsedAt: string | null } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!user.sleepFromHealth) return;
    void api.shortcutKey().then(setState).catch(() => setState(null));
  }, [user.sleepFromHealth, shown]);
  const url = `${window.location.origin}/api/shortcuts/sleep`;
  const when = (iso: string): string => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  async function copy(text: string, what: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      /* the text is on the screen to select */
    }
  }
  const hours = (from: number, to: number): number[] => Array.from({ length: (to - from) * 2 + 1 }, (_, i) => from + i / 2);
  return (
    <section className="stack-sm stack">
      <h2>Sleep from Health</h2>
      <p className="muted">
        An iPhone Shortcut can read last night’s sleep from Health and send the minutes here each morning. The
        app turns them into the word — short, okay or long — by the two boundaries below, and keeps the word,
        never the hours. A word you tapped yourself stays. Nothing else leaves the phone, and the key can write
        nothing but that word.
      </p>
      <label className="option option-toggle">
        <input type="checkbox" checked={user.sleepFromHealth} onChange={(e) => void api.updateProfile({ sleepFromHealth: e.target.checked }).then(() => refresh())} />
        <span><strong>Sleep from Health</strong><span className="muted"> — off by default</span></span>
      </label>
      {user.sleepFromHealth && (
        <>
          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <div className="field grow">
              <label htmlFor="short-under">Short night, under</label>
              <select id="short-under" className="input" value={user.shortNightUnder} onChange={(e) => void api.updateProfile({ shortNightUnder: Number(e.target.value) }).then(() => refresh())}>
                {hours(3, 8).map((h) => <option key={h} value={h}>{h} h</option>)}
              </select>
            </div>
            <div className="field grow">
              <label htmlFor="long-over">Long night, over</label>
              <select id="long-over" className="input" value={user.longNightOver} onChange={(e) => void api.updateProfile({ longNightOver: Number(e.target.value) }).then(() => refresh())}>
                {hours(7, 12).map((h) => <option key={h} value={h}>{h} h</option>)}
              </select>
            </div>
          </div>
          <div className="card stack-sm stack">
            <strong>The key</strong>
            {shown ? (
              <>
                <p className="muted" style={{ margin: 0 }}>Copy it now — it is shown once. Put it in the Shortcut as <code>key</code>.</p>
                <code style={{ wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>{shown}</code>
                <button type="button" className="btn btn-ghost btn-compact" onClick={() => void copy(shown, 'key')}>{copied === 'key' ? 'Copied' : 'Copy the key'}</button>
              </>
            ) : state?.hasKey && state.createdAt ? (
              <p className="muted" style={{ margin: 0 }}>Made {when(state.createdAt)}{state.lastUsedAt ? `, last used ${when(state.lastUsedAt)}` : ', not used yet'}. The key itself is not kept here.</p>
            ) : (
              <p className="muted" style={{ margin: 0 }}>No key yet.</p>
            )}
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-primary btn-compact" onClick={() => void api.makeShortcutKey().then((r) => setShown(r.key))}>{state?.hasKey ? 'Make a new key' : 'Make a key'}</button>
              {state?.hasKey && <button type="button" className="btn btn-ghost btn-compact" onClick={() => void api.forgetShortcutKey().then(() => { setShown(null); setState({ hasKey: false, createdAt: null, lastUsedAt: null }); })}>Forget it</button>}
            </div>
          </div>
          <div className="card stack-sm stack">
            <strong>The recipe</strong>
            <ol className="muted" style={{ margin: 0, paddingLeft: '1.2rem' }}>
              <li>Shortcuts → Automation → Time of Day, each morning, run immediately.</li>
              <li>Find Health Samples: Sleep, start date in the last 24 hours. Then Calculate Statistics → Sum of the samples’ duration, in minutes.</li>
              <li>Get Contents of URL: POST to <code>{url}</code>, request body JSON, with <code>key</code> set to your key and <code>minutes</code> set to that sum.</li>
            </ol>
            <button type="button" className="btn btn-ghost btn-compact" onClick={() => void copy(`POST ${url}\nContent-Type: application/json\n{"key": "<your key>", "minutes": <the sum>}`, 'req')}>{copied === 'req' ? 'Copied' : 'Copy the request'}</button>
          </div>
        </>
      )}
    </section>
  );
}
